import type { ReactNode } from 'react'

/**
 * Something went wrong, said the same way everywhere (D-515).
 *
 * <b>The app had roughly 140 error messages in 76 distinct class strings.</b>
 * Two families: a boxed one that replaces a screen, and a bare line beside a
 * field. The boxed family agreed nine times and differed thirty; the bare one
 * used `text-[12px]`, `text-[12.5px]`, `text-[13px]` and `text-sm`
 * interchangeably for the same job.
 *
 * <b>The part that actually mattered was not the size.</b> Of the error nodes
 * in the app, seventy-one announced themselves with `role="alert"` and fifteen
 * did not — and the fifteen were consistently the <em>screen-level</em> ones,
 * the "we could not load this" messages. An inline validation message at least
 * sits beside the field it is about; a page that is simply empty explains
 * nothing to somebody who cannot see it. Announcing is not a prop here, because
 * an error nobody is told about is not a decision worth offering.
 *
 * <b>Two shapes, not a size scale.</b> `boxed` replaces content and reads as a
 * state of the screen; the default sits under a control and reads as a note
 * about that control. Anything wanting a third shape is probably not an error.
 */
export default function ErrorNote({
  boxed = false,
  className = '',
  children,
}: {
  /** A whole-screen failure rather than a note beside a control. */
  boxed?: boolean
  /** Spacing for where it sits — never its colour, size or weight. */
  className?: string
  children: ReactNode
}) {
  const shape = boxed
    ? 'rounded-xl border border-crit bg-crit-bg px-4 py-3 text-sm text-crit'
    : 'text-[12.5px] text-crit'

  return (
    <p role="alert" className={`${shape} ${className}`.trim()}>
      {children}
    </p>
  )
}
